/** Tutorial Flow staged pipeline — shared step definitions. */

export const TUTORIAL_PIPELINE_STEPS = [
  {
    id: 'pass1',
    label: 'Pass 1 — Knowledge decomposition',
    shortLabel: 'Concept tree',
    weight: 10,
    tabId: 'pass1',
    kind: 'orchestration',
    description: 'Topic broken into a concept hierarchy (no artifacts yet).',
  },
  {
    id: 'pass2',
    label: 'Pass 2 — DFS artifact expansion',
    shortLabel: 'Artifacts',
    weight: 14,
    tabId: 'pass2',
    kind: 'orchestration',
    description: 'Each concept enriched with instructional artifacts.',
  },
  {
    id: 'pass2_optimize',
    label: 'Pass 2 optimize — dedupe & fill gaps',
    shortLabel: 'Optimize',
    weight: 8,
    tabId: 'pass2_optimize',
    kind: 'orchestration',
    description: 'Remove duplicate topics/explanations; add missing important ones (same JSON schema).',
  },
  {
    id: 'script',
    label: 'Pass 3 — Trainer script',
    shortLabel: 'Script',
    weight: 10,
    tabId: 'script',
    kind: 'orchestration',
    description: 'Full trainer narration for each topic in DFS order.',
  },
  {
    id: 'narration_audio',
    label: 'Narration audio (TTS)',
    shortLabel: 'Audio',
    weight: 12,
    tabId: 'narration_audio',
    kind: 'narration_audio',
    description: 'TTS from Pass 3 trainer script (deterministic skeleton — no LLM timeline yet).',
  },
  {
    id: 'word_align',
    label: 'WhisperX word alignment',
    shortLabel: 'Words',
    weight: 12,
    tabId: 'word_align',
    kind: 'word_align',
    description: 'Per-word timestamps and screenplay sync events from measured audio.',
  },
  {
    id: 'timeline',
    label: 'Timeline finalize',
    shortLabel: 'Timeline',
    weight: 18,
    tabId: 'timeline',
    kind: 'timeline',
    description: 'Finalize presentation using measured audio clocks — visuals, artifact sync, script_ref.',
  },
  {
    id: 'artifact_embed',
    label: 'Embed trainer artifacts',
    shortLabel: 'Artifacts sync',
    weight: 12,
    tabId: 'artifact_embed',
    kind: 'artifact_embed',
    description: 'Artifact render/disappear times and positions aligned to narration.',
  },
  {
    id: 'done',
    label: 'Ready for editor',
    shortLabel: 'Complete',
    weight: 4,
    tabId: 'done',
    kind: 'summary',
    description: 'Open the editor to preview, refine, and export.',
  },
];

export function pipelineProgressPct(currentStepId, status) {
  if (status === 'ready') return 100;
  const idx = TUTORIAL_PIPELINE_STEPS.findIndex((s) => s.id === currentStepId);
  if (idx < 0) return 0;
  return TUTORIAL_PIPELINE_STEPS.slice(0, idx + 1).reduce((sum, s) => sum + s.weight, 0);
}

export function stepIndex(stepId) {
  return TUTORIAL_PIPELINE_STEPS.findIndex((s) => s.id === stepId);
}

export function isStepComplete(stepId, project, orchestration) {
  if (!stepId || stepId === 'done') {
    return project?.narration_sync_phase === 'complete'
      || Boolean(
        project?.rendered_timeline?.narration_timings?.length
        && project?.rendered_timeline?.narration_word_timings?.length,
      );
  }
  if (stepId === 'artifact_embed') {
    return project?.narration_sync_phase === 'complete'
      || Boolean(project?.rendered_timeline?.trainer_artifacts_embedded);
  }
  if (stepId === 'word_align') {
    const phase = project?.narration_sync_phase;
    return phase === 'words' || phase === 'embedded' || phase === 'complete'
      || Boolean(project?.rendered_timeline?.narration_word_timings?.length);
  }
  if (stepId === 'narration_audio') {
    const clips = project?.narration_clip_assets || [];
    const hasClips = clips.length > 0;
    const phase = project?.narration_sync_phase;
    if (!hasClips) return false;
    return ['audio', 'words', 'embedded', 'complete'].includes(phase)
      || Boolean(project?.narration_audio_url);
  }
  if (stepId === 'narration') {
    return isStepComplete('artifact_embed', project, orchestration);
  }
  if (stepId === 'timeline') {
    return Boolean(
      project?.timeline?.timeline_finalized
      || project?.rendered_timeline?.timeline_finalized
      || (project?.timeline?.blocks?.length && project?.narration_sync_phase === 'complete'),
    );
  }
  if (stepId === 'script') {
    return Boolean(project?.script || Number(orchestration?.pipeline_pass) >= 3);
  }
  if (stepId === 'pass2_optimize') {
    return (orchestration?.pass_status?.pass2_optimize === 'complete'
      || orchestration?.pass_status?.pass2_optimize === 'fallback'
      || Boolean(orchestration?.pass_history?.pass2_optimize));
  }
  if (stepId === 'pass2') {
    return Number(orchestration?.pipeline_pass) >= 2;
  }
  if (stepId === 'pass1') {
    return Boolean(orchestration?.concept_tree?.length || Number(orchestration?.pipeline_pass) >= 1);
  }
  return false;
}

/**
 * Step icon state — completed steps stay done when a later step fails.
 */
export function pipelineStepStatus(stepId, {
  currentStepId,
  pipelineStatus,
  project = null,
  orchestration = null,
}) {
  if (stepId === 'done') {
    if (pipelineStatus === 'ready') return 'done';
    return 'pending';
  }
  if (isStepComplete(stepId, project, orchestration)) {
    return 'done';
  }
  if (pipelineStatus === 'working' && stepId === currentStepId) {
    return 'active';
  }
  if (pipelineStatus === 'error' && stepId === currentStepId) {
    return 'error';
  }
  return 'pending';
}

/** @deprecated Prefer pipelineStepStatus when project data is available. */
export function stepStatus(stepId, currentStepId, pipelineStatus) {
  return pipelineStepStatus(stepId, { currentStepId, pipelineStatus });
}

/** Merge orchestration pass_log with frontend-only timeline/narration entries. */
export function buildExtendedPassLog(orchestration, project) {
  const base = [...(orchestration?.pass_log || [])];
  const timeline = project?.timeline;
  const rendered = project?.rendered_timeline;

  if (timeline?.blocks?.length) {
    const blockCount = timeline.blocks.filter((b) => (b.type || '') !== 'scene_transition').length;
    const visualSkips = project?.visual_generation_skips || timeline?.visual_generation_skips || [];
    const mermaidFallbacks = project?.visual_mermaid_fallbacks || timeline?.visual_mermaid_fallbacks || [];
    const quotaNote = project?.visual_generation_quota_exhausted || timeline?.visual_generation_quota_exhausted ? ' (quota)' : '';
    const skipSuffix = visualSkips.length ? `, ${visualSkips.length} visual(s) skipped${quotaNote}` : '';
    const mermaidSuffix = mermaidFallbacks.length ? `, ${mermaidFallbacks.length} Mermaid diagram(s)${quotaNote}` : '';
    base.push({
      pass: 'timeline',
      status: visualSkips.length ? 'warning' : mermaidFallbacks.length ? 'complete' : 'complete',
      message: `Timeline — ${blockCount} content block(s), ${timeline.blocks.length} total row(s)${mermaidSuffix}${skipSuffix}`,
      stats: {
        block_count: blockCount,
        total_blocks: timeline.blocks.length,
        scene_count: (timeline.scenes || []).length || undefined,
        visuals_skipped: visualSkips.length || undefined,
        mermaid_fallbacks: mermaidFallbacks.length || undefined,
      },
      output_snapshot_key: 'timeline',
    });
  }

  if (project?.narration_audio_url) {
    base.push({
      pass: 'narration_audio',
      status: 'complete',
      message: `Narration audio — TTS from trainer script (${project.narration_sync_phase || 'audio'})`,
      stats: {
        narration_clips: (project.narration_clip_assets || []).length || undefined,
        total_audio_seconds: rendered?.total_duration,
      },
      output_snapshot_key: 'narration_audio',
    });
  }

  if (project?.rendered_timeline?.narration_word_timings?.length) {
    base.push({
      pass: 'word_align',
      status: 'complete',
      message: `WhisperX — ${project.rendered_timeline.word_timing_count || 0} word(s), ${project.rendered_timeline.screenplay_event_count || 0} event(s)`,
      stats: {
        word_timing_count: project.rendered_timeline.word_timing_count,
        screenplay_event_count: project.rendered_timeline.screenplay_event_count,
      },
      output_snapshot_key: 'word_align',
    });
  }

  if (project?.narration_sync_phase === 'complete' || project?.rendered_timeline?.trainer_artifacts_embedded) {
    base.push({
      pass: 'artifact_embed',
      status: 'complete',
      message: `Artifact embed — ${project.rendered_timeline?.trainer_artifacts_embedded_count || 0} placement(s) synced to audio`,
      output_snapshot_key: 'artifact_embed',
    });
  }

  const timings = rendered?.narration_timings || project?.narration_timings;
  if (timings?.length && !base.some((e) => e.pass === 'narration_audio')) {
    const totalSec = timings.reduce((s, t) => s + (Number(t.duration) || 0), 0);
    base.push({
      pass: 'narration',
      status: 'complete',
      message: `Narration sync — ${timings.length} clip(s), ${totalSec.toFixed(1)}s total audio`,
      stats: {
        narration_clips: timings.length,
        total_audio_seconds: Math.round(totalSec * 10) / 10,
      },
      output_snapshot_key: 'narration',
    });
  }

  return base;
}
